import React from 'react';
import { DerouleEditor } from '../ui/DerouleEditor';

/** Champ de la privatisation que la réponse à cette question doit pré-remplir automatiquement lors de sa
 *  création — voir `CreerPrivatisationPanel.tsx`. Compatibilité par type : 'date'/'start_time'/'end_time'
 *  n'ont de sens que pour une question 'datetime' (une date+heure unique) ; 'num_people' seulement pour
 *  une question 'text' (réponse libre parsée en nombre). Le serveur retire toute valeur incompatible. */
export type BriefChampLie = 'date' | 'start_time' | 'end_time' | 'num_people';

export interface BriefQuestion {
    id: string;
    question: string;
    type: 'text' | 'select' | 'datetime' | 'deroule';
    options: { id: string; label: string }[];
    allow_multiple: boolean;
    required: boolean;
    help_text?: string;
    champ_lie?: BriefChampLie | null;
}

/**
 * Le rendu des questions du brief — extrait de `DossierBriefPage.tsx` (2026-08-31) pour être partagé avec
 * le panneau du Pipeline (page 30) : le formulaire du brief s'y affiche désormais directement, au lieu de
 * renvoyer vers l'onglet Brief (page 31). Demande d'Emeni : « rester sur la même page de suivi et pouvoir
 * tout faire d'ici pour éviter de perdre le fil ». Un seul rendu, deux emplacements — jamais deux copies
 * qui finiraient par diverger.
 *
 * Purement affichage/saisie : l'appelant possède les valeurs et l'enregistrement.
 */
export function BriefQuestionsForm({ questions, valeurs, onChange }: {
    questions: BriefQuestion[];
    valeurs: Record<string, string>;
    onChange: (id: string, valeur: string) => void;
}) {
    if (questions.length === 0) {
        return <p className="text-xs text-content-muted">Ce questionnaire ne contient encore aucune question.</p>;
    }

    return (
        <>
            {questions.map((q) => (
                <div key={q.id} className="space-y-1.5">
                    <label className="text-[11px] font-bold text-content flex items-baseline gap-1">
                        {q.question}
                        {q.required && <span className="text-red-400">*</span>}
                    </label>
                    {q.help_text && <p className="text-[10px] text-content-muted">{q.help_text}</p>}

                    {q.type === 'deroule' ? (
                        <div className="bg-surface-soft border border-border rounded-xl p-3">
                            <DerouleEditor valeur={valeurs[q.id] || ''} onChange={(t) => onChange(q.id, t)} />
                        </div>
                    ) : q.type === 'select' ? (
                        // Les options viennent du modèle ; on n'en invente aucune. Le champ reste libre en
                        // secours si le client répond à côté de la liste — une conversation téléphonique ne
                        // tient pas toujours dans les cases prévues.
                        <select value={valeurs[q.id] || ''} onChange={(e) => onChange(q.id, e.target.value)}
                            className="w-full bg-surface border border-border rounded-lg px-2.5 py-1.5 text-sm text-content outline-none focus:border-brand-500">
                            <option value="">—</option>
                            {q.options.map((o) => <option key={o.id} value={o.label}>{o.label}</option>)}
                        </select>
                    ) : q.type === 'datetime' ? (
                        <input type="datetime-local" value={valeurs[q.id] || ''} onChange={(e) => onChange(q.id, e.target.value)}
                            className="w-full bg-surface border border-border rounded-lg px-2.5 py-1.5 text-sm text-content outline-none focus:border-brand-500" />
                    ) : (
                        <textarea value={valeurs[q.id] || ''} onChange={(e) => onChange(q.id, e.target.value)} rows={2}
                            className="w-full bg-surface border border-border rounded-lg px-2.5 py-1.5 text-sm text-content outline-none focus:border-brand-500 resize-none" />
                    )}
                </div>
            ))}
        </>
    );
}
